import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { gql } from "@apollo/client";

import client from "../../graphql/client";
import { useAuth } from "../../context/AuthContext";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { clearCart } from "../../state/slices/cartSlice";

import Breadcrumbs from "../../components/Breadcrumbs";
import MainNavbar from "../../pages/MainNavbar";
import Loading from "../../components/Loading";


// ============================================================
// PAYMENT STATUS QUERY
// ============================================================

type PaymentStatusResponse = {
  paymentStatus: {
    status: string;
  };
};

const PAYMENT_STATUS_QUERY = gql`
  query PaymentStatus($orderId: String!) {
    paymentStatus(orderId: $orderId) {
      status
    }
  }
`;


// ============================================================
// PROCESS LNG PURCHASE
// ============================================================

type ProcessPurchaseResponse = {
  processPurchase: {
    success: boolean;
    newIds: string[];
    logs: string[];
    message: string;
  };
};

const PROCESS_PURCHASE = gql`
  mutation ProcessPurchase(
    $orderId: ID!
    $isOffline: Boolean
  ) {
    processPurchase(
      orderId: $orderId
      isOffline: $isOffline
    ) {
      success
      newIds
      logs
      message
    }
  }
`;


// ============================================================
// STATUS CONSTANTS
// ============================================================

const STATUS = {
  COMPLETED: "completed",
  FAILED: "failed",
  PENDING: "pending",
};


// ============================================================
// NORMALIZE STATUS
// ============================================================

const normalizeStatus = (
  status?: string
) => {
  return status?.trim().toLowerCase() || "";
};


// ============================================================
// COMPONENT
// ============================================================

const PaymentStatus: React.FC = () => {

  const [searchParams] =
    useSearchParams();

  const navigate =
    useNavigate();

  const dispatch =
    useAppDispatch();


  // Prevent the initial request from
  // being started multiple times.

  const hasFetched =
    useRef(false);


  // Prevent processPurchase from being
  // called more than once for this page.

  const rewardProcessed =
    useRef(false);


  const {
    currentUser,
    token,
    loading: authLoading,
  } = useAuth();


  // ============================================================
  // ORDER ID
  // ============================================================

  const [orderId] = useState(() => {

    return (
      searchParams.get("orderId") ||
      localStorage.getItem("orderId") ||
      ""
    );

  });


  // ============================================================
  // STATE
  // ============================================================

  const [
    paymentStatus,
    setPaymentStatus,
  ] = useState<string | null>(null);

  const [
    loading,
    setLoading,
  ] = useState<boolean>(true);

  const [
    showSubnav,
    setShowSubnav,
  ] = useState(false);


  const isLoggedIn =
    !!currentUser;

  const userId =
    currentUser?.id || "";


  // ============================================================
  // PROCESS LNG PURCHASE
  // ============================================================

 const processPurchase = async (
  currentOrderId: string
): Promise<boolean> => {

  if (rewardProcessed.current) {
    console.log(
      "processPurchase already completed for:",
      currentOrderId
    );

    return true;
  }

  try {

    console.log(
      "======================================"
    );

    console.log(
      "PROCESSING LNG PURCHASE"
    );

    console.log(
      "ORDER ID:",
      currentOrderId
    );

    console.log(
      "TOKEN AVAILABLE:",
      !!token
    );

    console.log(
      "======================================"
    );

    if (!token) {
      throw new Error(
        "Authentication token is missing"
      );
    }

    const response =
      await client.mutate<ProcessPurchaseResponse>({
        mutation: PROCESS_PURCHASE,

        variables: {
          orderId: currentOrderId,
          isOffline: false,
        },

        context: {
          headers: {
            authorization: `Bearer ${token}`,
          },
        },
      });

    console.log(
      "PROCESS PURCHASE RESPONSE:",
      JSON.stringify(
        response.data,
        null,
        2
      )
    );

    if (response.errors?.length) {

      console.error(
        "PROCESS PURCHASE GRAPHQL ERRORS:",
        response.errors
      );

      throw new Error(
        response.errors
          .map(
            (error: any) =>
              error.message
          )
          .join(", ")
      );
    }

    const result =
      response.data?.processPurchase;

    if (!result) {
      throw new Error(
        "No processPurchase response received"
      );
    }

    console.log(
      "LNG PURCHASE RESULT:",
      result
    );

    if (!result.success) {

      console.error(
        "LNG reward processing failed:",
        result.message
      );

      return false;
    }

    rewardProcessed.current = true;

    console.log(
      "======================================"
    );

    console.log(
      "LNG PURCHASE SUCCESS"
    );

    console.log(
      "MESSAGE:",
      result.message
    );

    console.log(
      "NEW IDS:",
      result.newIds
    );

    console.log(
      "LOGS:",
      result.logs
    );

    console.log(
      "======================================"
    );

    return true;

  } catch (error: any) {

    console.error(
      "PROCESS PURCHASE ERROR:",
      error
    );

    return false;
  }
};


  // ============================================================
  // PAYMENT STATUS
  // ============================================================

  useEffect(() => {

    // Wait until authentication is ready.

    if (authLoading) {
      return;
    }


    // ==========================================================
    // LOGIN CHECK
    // ==========================================================

    if (!currentUser) {

      navigate("/login");

      return;

    }


    // ==========================================================
    // ORDER ID CHECK
    // ==========================================================

    if (!orderId) {

      setPaymentStatus(
        "invalid"
      );

      setLoading(false);

      return;

    }


    // ==========================================================
    // PREVENT DUPLICATE INITIAL REQUEST
    // ==========================================================

    if (
      hasFetched.current
    ) {

      return;

    }


    hasFetched.current =
      true;


    // ==========================================================
    // FETCH PAYMENT STATUS
    // ==========================================================

    const fetchStatus = async () => {

      try {

        console.log(
          "======================================"
        );

        console.log(
          "CHECKING PAYMENT STATUS"
        );

        console.log(
          "ORDER ID:",
          orderId
        );

        console.log(
          "======================================"
        );


        const {
          data,
        } =
          await client.query<
            PaymentStatusResponse
          >({

            query:
              PAYMENT_STATUS_QUERY,

            variables: {
              orderId,
            },

            fetchPolicy:
              "no-cache",

            context: {

              headers: {

                authorization:
                  `Bearer ${token}`,

              },

            },

          });


        // ======================================================
        // NORMALIZE STATUS
        // ======================================================

        const status =
          normalizeStatus(
            data?.paymentStatus?.status
          );


        console.log(
          "PAYMENT STATUS:",
          status
        );

        console.log(
          "PAYMENT STATUS RESPONSE:",
          data?.paymentStatus
        );


        setPaymentStatus(
          status || "error"
        );


        // ======================================================
        // PAYMENT COMPLETED
        // ======================================================

        if (
          status === STATUS.COMPLETED
        ) {

          console.log(
            "PAYMENT COMPLETED"
          );


          // ----------------------------------------------------
          // PROCESS LNG REWARD
          // ----------------------------------------------------

          const rewardSuccess =
            await processPurchase(
              orderId
            );


          // ----------------------------------------------------
          // IMPORTANT
          // ----------------------------------------------------
          //
          // Payment is already completed.
          //
          // If reward processing fails, DO NOT show
          // "Payment Failed".
          //
          // The order payment is successful.
          //

          if (!rewardSuccess) {

            console.error(
              "Payment completed but LNG reward processing failed."
            );


            /*
             * Do not clear the cart here.
             *
             * The user can retry reward processing later.
             */

            return;

          }


          // ----------------------------------------------------
          // CLEAR CART
          // ----------------------------------------------------

          await dispatch(
            clearCart(userId)
          );


          // ----------------------------------------------------
          // REDIRECT TO ORDERS
          // ----------------------------------------------------

          setTimeout(() => {

            navigate(
              `/user/trackorders/${userId}?refresh=${Date.now()}`
            );


            localStorage.removeItem(
              "orderId"
            );

            localStorage.removeItem(
              "mtransactionId"
            );

          }, 2000);


          return;

        }


        // ======================================================
        // PAYMENT FAILED
        // ======================================================

        if (
          status === STATUS.FAILED
        ) {

          console.log(
            "PAYMENT FAILED"
          );

          return;

        }


        // ======================================================
        // PAYMENT PENDING
        // ======================================================

        if (
          status === STATUS.PENDING
        ) {

          console.log(
            "PAYMENT STILL PENDING"
          );


          setTimeout(
            fetchStatus,
            3000
          );


          return;

        }


        // ======================================================
        // UNKNOWN STATUS
        // ======================================================

        console.warn(
          "UNKNOWN PAYMENT STATUS:",
          status
        );

      } catch (error) {

        console.error(
          "PAYMENT STATUS ERROR:",
          error
        );


        setPaymentStatus(
          "error"
        );

      } finally {

        setLoading(
          false
        );

      }

    };


    fetchStatus();


    // ==========================================================
    // CLEANUP
    // ==========================================================

    return () => {
      // No timer reference to clear here.
      // Existing behavior is preserved.
    };


  }, [
    orderId,
    authLoading,
    currentUser,
    dispatch,
    navigate,
    token,
    userId,
  ]);


  // ============================================================
  // RENDER CONTENT
  // ============================================================

  const renderContent = () => {


    // ==========================================================
    // LOADING
    // ==========================================================

    if (
      loading ||
      authLoading
    ) {

      return <Loading />;

    }


    // ==========================================================
    // COMPLETED
    // ==========================================================

    switch (
      paymentStatus
    ) {

      case STATUS.COMPLETED:

        return (

          <div className="check_user_order_success">

            <div className="check_user_success_animation">

              <svg
                className="check_user_checkmark"
                viewBox="0 0 52 52"
              >

                <circle
                  className="check_user_checkmark_circle"
                  cx="26"
                  cy="26"
                  r="25"
                  fill="none"
                />

                <path
                  className="check_user_checkmark_check"
                  fill="none"
                  d="M14 27l7 7 16-16"
                />

              </svg>

            </div>


            <h2>
              Order Placed Successfully!
            </h2>


            <p>
              Reward processing completed.
              Redirecting to your orders...
            </p>

          </div>

        );


      // ========================================================
      // FAILED
      // ========================================================

      case STATUS.FAILED:

        return (

          <div className="check_user_order_success">

            <h2
              style={{
                color: "#e74c3c",
              }}
            >
              Payment Failed
            </h2>


            <p>
              Please try again.
            </p>


            <button
              onClick={() =>
                navigate(
                  "/user/checkout"
                )
              }
            >
              Try Again
            </button>

          </div>

        );


      // ========================================================
      // PENDING
      // ========================================================

      case STATUS.PENDING:

        return (

          <div className="check_user_order_success">

            <h2
              style={{
                color: "#f39c12",
              }}
            >
              Payment Pending
            </h2>


            <p>
              Waiting for confirmation...
            </p>

          </div>

        );


      // ========================================================
      // INVALID
      // ========================================================

      case "invalid":

        return (

          <div className="check_user_order_success">

            <h2>
              Invalid Request
            </h2>


            <p>
              Order ID is missing.
            </p>

          </div>

        );


      // ========================================================
      // ERROR
      // ========================================================

      default:

        return (

          <div className="check_user_order_success">

            <h2>
              Error
            </h2>


            <p>
              Unable to fetch payment status.
            </p>

          </div>

        );

    }

  };


  // ============================================================
  // MAIN UI
  // ============================================================

  return (

    <div>

      <MainNavbar
        searchQuery=""
        setSearchQuery={() => {}}
        isLoggedIn={isLoggedIn}
        showSubnav={showSubnav}
        setShowSubnav={
          setShowSubnav
        }
        userId={userId}
        disableSearch={true}
      />


      <Breadcrumbs />


      <div
        className="check_user_container"
      >

        {
          renderContent()
        }

      </div>

    </div>

  );

};


export default PaymentStatus;